import { RunnerStep, TestStepResult } from '../../models/test-run.model';

/**
 * Dieselbe Schreibweise wie {@code VariableContext} in der Engine
 * (`engine-core/src/engine/variables.ts`). Sie laesst sich vom Frontend aus nicht
 * importieren, deshalb nachgebildet: Weicht sie ab, zeigt der manuelle Runner einen
 * anderen Text als der automatisierte Lauf eintippt.
 */
const PLACEHOLDER = /\{([^}]+)\}/gu;

/** Was die Auffloesung von einem Schritt braucht — gleich, ob Runner-Schritt oder Schrittergebnis. */
export interface IterationNode {
  id: string;
  parentStepResultId: string | null;
  iterationValues?: Record<string, string> | null;
}

/** Setzt die Werte der geltenden Zeile in einen Text ein. */
export type TextFill = (text: string | null) => string | null;

/**
 * Setzt die Werte der Datenzeile in Schritttext und Soll-Ergebnis ein (NEX-1090) — im
 * manuellen Runner, in der Ergebnis-Ansicht des Durchlaufs und im Testergebnis-Detail
 * (NEX-1677). Alle drei nutzen diese Funktion, damit sie dieselben Texte zeigen.
 *
 * <p>Ein Modulaufruf mit gepinntem Testdatenblatt laeuft einmal je Zeile. Welche
 * Werte fuer einen Durchlauf gelten, haengt das Backend an den Modulknoten
 * ({@code TestRunResolver}); gebraucht werden sie in dessen Schritten. Der manuelle
 * Lauf zeigt dort Beschreibung und Soll-Ergebnis, nicht die TCDL-Zeile — ohne
 * Einsetzung muesste der Ausfuehrende die Werte im Datenblatt nachschlagen.
 *
 * <p>Ein Platzhalter ohne passende Spalte bleibt stehen. So sieht der Ausfuehrende,
 * dass im Datenblatt etwas fehlt, statt eine leere Stelle vorzufinden.
 *
 * <p>Die Werte des Modulknotens gelten fuer seine Schritte, nicht fuer ihn selbst:
 * Seine eigene Zeile ist die Gruppen-Ueberschrift, die traegt keine Werte.
 *
 * <p>Welche Textfelder gefuellt werden, gibt {@code fillTexts} vor; ohne sie die Felder
 * eines {@link RunnerStep}. Ein Schritt, fuer den keine Zeile gilt, kommt unveraendert zurueck.
 */
export function resolveIterationValues(steps: readonly RunnerStep[]): RunnerStep[];
export function resolveIterationValues<T extends IterationNode>(
  steps: readonly T[],
  fillTexts: (step: T, fill: TextFill) => T,
): T[];
export function resolveIterationValues<T extends IterationNode>(
  steps: readonly T[],
  fillTexts?: (step: T, fill: TextFill) => T,
): T[] {
  const fillStep = fillTexts ?? (fillRunnerStep as unknown as (step: T, fill: TextFill) => T);
  const byId = new Map(steps.map((step) => [step.id, step]));
  return steps.map((step) => {
    const values = inheritedValues(step, byId);
    if (!values) return step;
    return fillStep(step, (text) => fill(text, values));
  });
}

/** Fuellt Aktion und Soll eines Schrittergebnisses; Ist und Kommentar bleiben, wie der Tester sie eingetragen hat. */
export function fillStepResult(step: TestStepResult, fill: TextFill): TestStepResult {
  return {
    ...step,
    descriptionSnapshot: fill(step.descriptionSnapshot),
    expectedSnapshot: fill(step.expectedSnapshot),
  };
}

function fillRunnerStep(step: RunnerStep, fill: TextFill): RunnerStep {
  const filled = { ...step, description: fill(step.description), expected: fill(step.expected) };
  if (step.title !== undefined) filled.title = fill(step.title) ?? undefined;
  return filled;
}

/**
 * Die Zeilen aller Modulknoten oberhalb, uebereinandergelegt — {@code null}, wenn keiner
 * eine traegt.
 *
 * <p>Zusammengefuehrt statt beim ersten Treffer abgebrochen: Bei zwei Datenblaettern
 * uebereinander legt das Backend an jedem Modulknoten nur dessen eigene Zeile ab und
 * reicht den zusammengefuehrten Stand allein nach unten weiter
 * ({@code TestRunResolver}: {@code innerer.putAll(row.values())}). Wer nur die naechste
 * Zeile naehme, verloere die Spalten des aeusseren Blatts. Die naehere Zeile gewinnt —
 * dieselbe Rangfolge wie {@code VariableContext#withRow}.
 */
function inheritedValues(step: IterationNode, byId: Map<string, IterationNode>): Record<string, string> | null {
  const chain: Record<string, string>[] = [];
  const seen = new Set<string>([step.id]);
  let parentId = step.parentStepResultId;
  while (parentId !== null && !seen.has(parentId)) {
    const parent = byId.get(parentId);
    if (!parent) break;
    if (parent.iterationValues) chain.push(parent.iterationValues);
    seen.add(parentId);
    parentId = parent.parentStepResultId;
  }
  if (chain.length === 0) return null;
  // Von aussen nach innen: chain laeuft von nah nach fern, deshalb rueckwaerts.
  return Object.assign({}, ...chain.reverse());
}

function fill(text: string | null, values: Record<string, string>): string | null {
  if (!text) return text;
  // Object.hasOwn: Ein Platzhalter wie {constructor} ohne Spalte bleibt stehen, statt ein
  // geerbtes Feld von Object.prototype einzusetzen.
  return text.replace(PLACEHOLDER, (placeholder, name: string) =>
    Object.hasOwn(values, name) ? (values[name] ?? placeholder) : placeholder,
  );
}
